import {
  Component,
  OnInit,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
  DestroyRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Signal,
  computed
} from '@angular/core';

import { MapService } from '../../services/map.service';
import { FormControl } from '@angular/forms';
import { LocationInterface } from '../../../shared/interfaces/location-interface';
import { ShopListInterface } from '../../../shared/interfaces/shop-list-interface';
import { filter } from 'rxjs';
import {
  PlatformInformationService,
  PlatformurlPipe
} from '../../../../../projects/platform-manager/src/public_api';
import { SsrCookieService as CookieService } from 'ngx-cookie-service-ssr';
import { ShopService, eventTimeType } from '../../../shared/services/shop.service';
import { BookingParametersInterface } from '../../../shared/entities/bookingParameters';
import { GoogleMap, GoogleMapsModule, MapInfoWindow } from '@angular/google-maps';
import { provideIcons } from '../../../shared/icons/icon-provider';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { user, infoCircle, clock, stop, cross, IconComponent, hotelPin } from '../../../shared/icons';
import { MapOverlayDirective } from '../../../shared/directives/map-overlay.directive';
import { UnbookableInfoComponent } from '../../../shared/saleslocation/shop/unbookable-info/unbookable-info.component';
import { DatePipe } from '@angular/common';
import { DistanceDisplayComponent } from '../../../shared/components/distance-display/distance-display.component';
import { SelectedHotelService } from '../../../shared/services/selected-hotel.service';
import { Shop } from '../../../shared/entities/shop';
import { ResolutionService } from '../../../core/services/resolution.service';
import { CurrencyService } from '../../../core/services/currency.service';
import { CurrencyPipe } from '../../../core/pipes/currency.pipe';
import { AlpitranslatePipe } from '../../../core/pipes/alpitranslate.pipe';
import { ShopFilterService } from '../../../core/services/shop-filter.service';
import { CrowdinTranslationService } from '../../services/crowdin-translation.service';
import { loaderMap } from './translations/loader-map';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  imports: [
    IconComponent,
    MapOverlayDirective,
    CurrencyPipe,
    UnbookableInfoComponent,
    DatePipe,
    AlpitranslatePipe,
    GoogleMapsModule,
    // lazy import
    DistanceDisplayComponent,
    PlatformurlPipe
  ],
  providers: [provideIcons({ infoCircle, user, clock, stop, cross, hotelPin })],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapComponent implements OnInit {
  get ages() {
    return this.bookingParameters.ages;
  }

  get adults() {
    return this.ages.filter(age => age >= 18);
  }

  get children() {
    return this.ages.filter(age => age <= 17);
  }

  @Input() centerpoint: { lat: number; lng: number };
  @Input() shops: Array<Shop>;
  @Input() zoom: number;
  @Input() height = { height: '75vh' }; // fix set height for map
  @Input() fixedHeight = false;
  @Input() mapType = 'town';
  @Output() selectedShop: EventEmitter<ShopListInterface> = this.mapService.shopChange;
  @ViewChild('map', { static: false }) map: GoogleMap;
  @ViewChildren(MapInfoWindow) infoWindows: QueryList<MapInfoWindow>;

  private directionsRenderer: google.maps.DirectionsRenderer;

  mapOptions: google.maps.MapOptions = {
    clickableIcons: false,
    fullscreenControl: false,
    gestureHandling: 'greedy',
    streetViewControl: false,
    mapTypeControlOptions: {
      mapTypeIds: [] // Only show the standard roadmap
    }
  };
  usePanning = true;
  searchControl: FormControl<string>; // TODO: wrap with form, use ReactiveFormControl
  latitude: number; // used for user hotel
  longitude: number; // used for user hotel
  hotelIcon = {
    url: 'assets/images/home2.png',
    scaledSize: {
      height: 36,
      width: 25
    }
  };
  phone: { [key: string]: string };
  email: string;
  bookingParameters: BookingParametersInterface;

  constructor(
    public mapService: MapService,
    private platformService: PlatformInformationService,
    protected resolutionService: ResolutionService,
    public cookieService: CookieService,
    public currencyService: CurrencyService,
    private shopService: ShopService,
    protected shopFilterService: ShopFilterService,
    protected selectedHotelService: SelectedHotelService,
    private destroyRef: DestroyRef,
    private cdr: ChangeDetectorRef,
    private crowdinTranslationService: CrowdinTranslationService
  ) {
    this.centerpoint = { lat: 0, lng: 0 };
  }

  ngOnInit() {
    this.crowdinTranslationService.watchLangChange({ loaderMap: loaderMap, destroyRef: this.destroyRef });
    this.selectedHotelService.onHotelChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      this.updateCenterpoint(this.mapService.lastPoint);
    });
    this.mapService.pointChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(point => this.updateCenterpoint(point));
    if (this.mapService.lastPoint) {
      this.updateCenterpoint(this.mapService.lastPoint);
    }
    this.mapService.mapsApiLoaded.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.mapService.lastPoint) {
        this.updateCenterpoint(this.mapService.lastPoint);
      }
    });
    this.mapService.loadMapsApi();
    this.shopService.updates
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(event => event.type === eventTimeType.onInTown)
      )
      .subscribe(data => {
        this.bookingParameters = data.data.bookingParameters;
      });
    this.bookingParameters = this.shopService.bookingParameters.copy();
    if (!this.zoom) {
      this.zoom = 15;
    }
    this.platformService.data$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      this.phone = data.hotlines;
      this.email = data.info_email;
    });
  }

  getFilteredSignal(shop: { distance: number; inTown: boolean }): Signal<boolean> {
    return computed(() => {
      return (
        this.shopFilterService.filterBy(
          [shop],
          this.shopFilterService.distance(),
          this.shopFilterService.activeFilters()
        ).length < 1
      );
    });
  }

  /**
   * istanbul ignore next
   * @param point lat/lng to center the map on
   */
  updateCenterpoint(point: LocationInterface) {
    if (this.shops) {
      this.shops.forEach(shop => {
        if (point.longitude === shop.location.longitude && point.latitude === shop.location.latitude) {
          shop.showOnMap = true;
          if (shop.inTown) {
            this.zoom = 15;
          } else {
            this.zoom = 12;
          }
          setTimeout(() => {
            if (this.directionsRenderer) {
              this.directionsRenderer.setDirections({ routes: [], request: null });
            }
            const hotel = this.selectedHotelService.selectedHotel();
            if (hotel) {
              // add navigation route
              this.addNavigationRoute(
                { lat: shop.location.latitude, lng: shop.location.longitude },
                { lat: hotel.geometry.location.lat(), lng: hotel.geometry.location.lng() }
              );
            }
          }, 800); // offsets animation timings of map
        } else {
          // only close if not single shop on map. It helps visualize on the product page map
          if (this.shops.length > 1) {
            shop.showOnMap = false;
          }
        }
      });
    }
    setTimeout(() => {
      this.centerpoint.lat = +point.latitude;
      this.centerpoint.lng = +point.longitude;
      // no more polluting change detection cycles, we need to keep the template in check here
      this.cdr.markForCheck();
    }, 500); // offsets animation timing when opening/closing map icons or hotel selection
  }

  toggleDetails(shop: ShopListInterface) {
    this.shops.forEach(loopShop => {
      loopShop.showOnMap = false;
    });
    shop.showOnMap = !shop.showOnMap;
    if (shop.showOnMap) {
      if (this.directionsRenderer) {
        this.directionsRenderer.setDirections({ routes: [], request: null });
      }
      const hotel = this.selectedHotelService.selectedHotel();
      if (hotel) {
        // add navigation route
        this.addNavigationRoute(
          { lat: shop.location.latitude, lng: shop.location.longitude },
          { lat: hotel.geometry.location.lat(), lng: hotel.geometry.location.lng() }
        );
      }
    }
  }

  isShopFiltered(shop: ShopListInterface): boolean {
    return (
      this.shopFilterService.filterBy(
        [shop],
        this.shopFilterService.distance(),
        this.shopFilterService.activeFilters()
      ).length < 1
    );
  }

  /**
   * the user clicked on the link in the infobox.
   * hide overlay map, scroll to shop
   * @param $event click event
   * @param shop the selected shop
   */
  clickedInfoBox($event, shop: ShopListInterface) {
    $event.preventDefault();
    // if the shop is filtered, unset filters so the shop will be visible before scrolling to it
    if (this.isShopFiltered(shop)) {
      this.shopFilterService.activeFilters.set([]);
      this.shopFilterService.distance.set(this.shopFilterService.maxDistance);
    }
    this.selectedShop.emit(shop);
    this.mapService.selectedShop(shop);
    this.cdr.detectChanges();
  }

  addNavigationRoute(
    origin: { lat: number; lng: number },
    destination: { lat: number; lng: number },
    mode = google.maps.TravelMode.WALKING
  ) {
    const directionsService = new google.maps.DirectionsService();
    this.directionsRenderer = this.directionsRenderer ?? new google.maps.DirectionsRenderer();
    // Bind the DirectionsRenderer to the map
    this.directionsRenderer.setMap(this.map.googleMap);

    // Set route options
    const request = {
      origin: `${origin.lat},${origin.lng}`,
      destination: `${destination.lat},${destination.lng}`,
      travelMode: mode // Options: DRIVING, WALKING, TRANSIT, BICYCLING
    };

    // Calculate and display the route
    directionsService.route(request, (result, status) => {
      if (status === google.maps.DirectionsStatus.OK) {
        this.directionsRenderer.setDirections(result);
      } else {
        console.error('Error fetching directions: ' + status);
      }
    });
  }
}
